import {
  Box,
  Button,
  Collapse,
  darken,
  Drawer,
  IconButton,
  keyframes,
  Paper,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import {
  OpeningHoursDay,
  UpsellFlow,
  UpsellForm,
  useDeleteUpsellMutation,
  useFetchUpsellQuery,
  useFetchUpsellsQuery,
  useToggleUpsellActiveMutation,
  useUpdateUpsellTriggersMutation,
} from "../../store/api/upsellApi";
import { usePagination } from "./modernTS/JUI/Pagination/usePagination";
import { PushPin } from "@mui/icons-material";
import { Add } from "@mui/icons-material";
import "swiper/css/bundle";
import { blue, cyan, green, grey, orange, purple, red, yellow } from "@mui/material/colors";
import { useCreateUpsell } from "./Forms/VariantUpsell/useCreateUpsell";
import ModelInstance from "./modernTS/JUI/ModelInstance";
import { PinnableItemsProvider } from "./modernTS/JUI/PinnableItems/PinnableItemsProvider";
import Pinnable from "./modernTS/JUI/PinnableItems/Pinnable";
import { usePinnableItems } from "./modernTS/JUI/PinnableItems/usePinnableItems";
import { FlowEditPage } from "./Forms/VariantUpsell/FlowEditPage";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSalesareaCtx } from "./modernTS/salesarea/SalesareaContext";
import _ from "lodash";
import { Pagination } from "./modernTS/JUI/Pagination/Pagination";
import Upsell from "../Vestigingen/Tabs/Upsell";
import { useHookForm } from "mui-react-hook-form-plus";
import UpsellTriggers from "./Forms/Misc/UpsellTriggers";
import { FormProvider } from "react-hook-form";
import { UpsellTriggersData } from "./Forms/VariantUpsell/UpsellAddTriggersStep";
import { TypedUseQueryHookResult } from "@reduxjs/toolkit/query/react";

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(value, min));
}

const openingHoursDays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function useTimetableDimensions() {
  const [table, setTable] = useState({ width: 0, height: 0, left: 0, top: 0 });

  useEffect(() => {
    const updateDimensions = _.debounce(() => {
      if (anchorFirstDay == null || anchorLastDay == null || anchorTable == null) return;

      const { bottom: firstDayBottom, left: firstDayLeft } = anchorFirstDay.getBoundingClientRect();
      const { right: lastDayRight } = anchorLastDay.getBoundingClientRect();
      const { bottom: tableBottom, left: tableLeft } = anchorTable.getBoundingClientRect();

      setTable({
        width: lastDayRight - firstDayLeft,
        // 16px = padding
        height: tableBottom - firstDayBottom - 16,
        // 16 * 2 = padding
        left: firstDayLeft - tableLeft + 16 * 2,
        top: firstDayBottom,
      });
    }, 1000 / 60);

    const anchorFirstDay = document.querySelector("#anchor-first-day");
    const anchorLastDay = document.querySelector("#anchor-last-day");
    const anchorTable: HTMLDivElement | null = document.querySelector("#anchor-table");
    const mutObserver = new MutationObserver(updateDimensions);
    const sizeObserver = new ResizeObserver(updateDimensions);
    if (anchorTable) {
      sizeObserver.observe(anchorTable);
      mutObserver.observe(anchorTable, {
        childList: true,
        subtree: true,
      });
    } else {
      console.error("Parent table was loaded after TimeTable");
    }

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
      mutObserver.disconnect();
      sizeObserver.disconnect();
    };
  }, []);

  /**
   * Translates from a {day, timerange, idx}-tuple to a position and width on the timetable
   * For instance, {"saturday", "9:00-12:30"} will be translated into the correct "position" on the timetable's saturday
   * @param day
   * @param range
   * @param rangeIdx
   */
  function timeRangeToDimensions(day: OpeningHoursDay, range: string, rangeIdx: number) {
    const dayWidth = table.width / 7;
    const dayIdx = openingHoursDays.findIndex((d) => d === day);

    const [timeStart, timeEnd] = range.split("-");
    const [hoursStart, minutesStart] = timeStart.split(":").map((str) => Number.parseInt(str, 10));
    const [hoursEnd, minutesEnd] = timeEnd.split(":").map((str) => Number.parseInt(str, 10));

    const minutesStarting = hoursStart * 60 + minutesStart;
    const minutesEnding = hoursEnd * 60 + minutesEnd;

    const minuteWidth = dayWidth / (24 * 60);
    const startPos = minutesStarting * minuteWidth;
    const endPos = minutesEnding * minuteWidth;

    let width = endPos - startPos;
    const left = dayIdx * dayWidth + startPos;
    const top = `calc((1rem + 4px) * ${rangeIdx} + 4px)`;

    const isTiny = minutesEnding - minutesStarting < 60;
    // very small timeranges should be visualised as a circle
    if (isTiny) {
      width = 8;
    }
    return {
      left,
      width,
      isTiny,
      top,
    };
  }

  function calculateRowHeight(numberOfTimeschedules: number) {
    return `calc((1rem + 4px) * ${numberOfTimeschedules} + 8px)`;
  }

  return { table, timeRangeToDimensions, calculateRowHeight };
}

function TimeTable({ dimensions }: { dimensions: ReturnType<typeof useTimetableDimensions> }) {
  const dayWidth = dimensions.table.width / 7;

  return (
    <Box
      sx={{
        position: "absolute",
        width: dimensions.table.width,
        height: dimensions.table.height,
        left: dimensions.table.left,
        top: dimensions.table.top,
      }}
    >
      {Array(8)
        .fill(0)
        .map((_, dayIdx) => (
          <Fragment key={dayIdx}>
            <div
              style={{
                position: "absolute",
                bottom: 0,
                top: 0,
                border: "1px solid #bbb",
                left: `${dayIdx * dayWidth}px`,
              }}
            />
            {dayIdx < 7
              ? [6, 12, 18].map((hour) => (
                  <div
                    key={hour}
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      border: "1px solid #eee",
                      left: `${dayIdx * dayWidth + (hour / 24) * dayWidth}px`,
                    }}
                  />
                ))
              : null}
          </Fragment>
        ))}
    </Box>
  );
}

const colorMap = (idx: number) => {
  const colors = [red, grey, green, yellow, cyan, purple, orange];

  return colors[idx % colors.length][700];
};

const timeStyle = (bgColor: string) => ({
  backgroundColor: bgColor,
  color: darken(bgColor, 0.4),
  fontWeight: "bold",
  borderRadius: "12px",
  textAlign: "center",
  zIndex: 10,
  position: "absolute",
  py: "2px",
  userSelect: "none",
});

function Flow({
  flows,
  dimensions,
  parentUpsell,
}: {
  flows: UpsellFlow[];
  dimensions: ReturnType<typeof useTimetableDimensions>;
  parentUpsell: Upsell;
}) {
  const isAlwaysActive = flows?.length === 1 && flows[0].time_schedule == null;

  if (isAlwaysActive) {
    return (
      <Box sx={{ position: "relative" }}>
        <Pinnable model={flows[0]} name={"flow"}>
          <Box
            sx={{
              ...timeStyle(blue[700]),
              width: `${dimensions.table.width - 2}px`,
              left: 0,
              "&:hover": {
                cursor: "pointer",
              },
            }}
          >
            Always active
          </Box>
        </Pinnable>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "absolute" }}>
      {flows?.map((flow, idx) => {
        // This is only here to make typescript happy. The upsell type is spaghetti'd together
        // with the upsell form type. Need to fix that sometime
        if (flow.time_schedule.opening_hours == null) return <>opening hours null</>;

        const timeschedule = flow.time_schedule;
        const { id: timescheduleIdx } = timeschedule;

        const flows = Object.entries(flow.time_schedule.opening_hours).map(([day, timeRanges]) => {
          return (
            <Fragment key={day}>
              {timeRanges.map((range, rangeIdx) => {
                const { left, width, isTiny, top } = dimensions.timeRangeToDimensions(
                  day as OpeningHoursDay,
                  range,
                  idx
                );
                const color = colorMap(timescheduleIdx);
                return (
                  <Tooltip
                    key={rangeIdx}
                    title={
                      <Box>
                        <Typography fontSize={"1.7em"} variant={"h4"}>
                          <ModelInstance instance={timeschedule} modelName={"Timeschedule"} />
                        </Typography>
                        <Typography component={"span"} variant={"body1"}>
                          {day}
                        </Typography>{" "}
                        <Typography component={"span"} variant={"body1"}>
                          {range}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        ...timeStyle(color),
                        opacity: timeschedule.is_active ? 1 : 0.6,
                        left: clamp(left, 0, dimensions.table.width - 18),
                        width,
                        minHeight: 16,
                        ...(isTiny
                          ? {
                              width: 16,
                              borderRadius: "50%",
                            }
                          : {}),
                        top,
                        textAlign: "center",
                      }}
                    >
                      {width < 80 ? "" : range}
                    </Box>
                  </Tooltip>
                );
              })}
            </Fragment>
          );
        });
        return (
          <Pinnable model={flow} name={"flow"}>
            {flows}
          </Pinnable>
        );
      })}
    </Box>
  );
}

const tiltAnimation = keyframes`
    0% {
        transform: rotate(0deg);
    }
    25% {
        transform: rotate(30deg);
        color: ${red[700]};
    }
    75% {
        transform: rotate(30deg);
        color: ${red[700]};
    }
    100% {
        transform: rotate(0deg);
    }
`;

function TriggersEditPage({ upsellId }: { upsellId: UpsellForm["id"] }) {
  const query = useFetchUpsellQuery({ upsellId });

  const upsell = query.data?.data;

  const form = useHookForm<UpsellTriggersData & { isLoading: boolean }>({
    defaultValues: {
      isLoading: true,
      triggers: {
        menus: [],
        products: [],
      },
    },
  });
  const formLoading = form.watch("isLoading");
  const [updateTriggers] = useUpdateUpsellTriggersMutation();

  useEffect(() => {
    if (query.isSuccess && query.data && formLoading) {
      form.reset({
        isLoading: false,
        triggers: {
          menus: query.data.data.trigger_menus,
          products: query.data.data.trigger_products,
        },
      });
    }
  }, [form, form.reset, query.isSuccess, query.data, formLoading]);

  if (query.isLoading || query.isFetching) {
    return <>Loading</>;
  }

  if (query.isError) {
    return <>Error</>;
  }

  if (formLoading) {
    return <>Loading after fetch</>;
  }

  return (
    <FormProvider {...form}>
      <Typography fontSize={"1.8em"} sx={{ mb: 4 }} variant={"h4"}>
        Triggers for <ModelInstance instance={upsell} mark={"none"} modelName={"Upsell"} showIdWhen={"always"} />
      </Typography>
      <UpsellTriggers
        onChange={async (triggers) => {
          console.log(triggers);
          try {
            await updateTriggers({
              upsell_id: upsell.id,
              triggers: {
                menus: triggers.menus.map(({ id }) => id),
                products: triggers.products.map(({ id }) => id),
              },
            }).unwrap();
            toast.success(() => (
              <>
                Modified the triggers for{" "}
                <ModelInstance
                  inheritColor
                  instance={upsell}
                  mark={"none"}
                  modelName={"Upsell"}
                  showIdWhen={"always"}
                />{" "}
                successfully.
              </>
            ));
          } catch (e) {
            toast.error(() => (
              <>
                Was unable to modify triggers for{" "}
                <ModelInstance
                  inheritColor
                  instance={upsell}
                  mark={"none"}
                  modelName={"Upsell"}
                  showIdWhen={"always"}
                />
              </>
            ));
          }
        }}
      />
    </FormProvider>
  );
}

function MiniDrawer() {
  const { pinnedItem, hoveredItem, unpin } = usePinnableItems();

  const isPinned = Boolean(pinnedItem);
  const isHovered = Boolean(hoveredItem);

  function selectPage(name: string, model: any) {
    switch (name) {
      case "flow":
        return <FlowEditPage flow={model as UpsellFlow} key={model.id} />;
      case "triggers":
        return <TriggersEditPage key={model.id} upsellId={model.id} />;
    }

    return <></>;
  }

  let page = <Typography variant={"overline"}>Select something first</Typography>;
  if (hoveredItem) {
    page = selectPage(hoveredItem.item, hoveredItem.model);
  } else if (pinnedItem) {
    page = selectPage(pinnedItem.item, pinnedItem.model);
  }

  return (
    <Drawer
      anchor={"bottom"}
      open={true}
      sx={{
        "& .MuiDrawer-paper": {
          translate: "calc(50vw - 50%)",
          width: isPinned ? "50vw" : "25vw",
          maxHeight: "70vh",
          overflowY: "auto",
          p: 1,
          transition: "width 200ms ease-in-out",
        },
      }}
      variant={"permanent"}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        {hoveredItem && !pinnedItem ? (
          <Typography fontSize={"1.8em"} variant={"h4"}>
            <ModelInstance instance={hoveredItem.model} modelName={"UpsellFlow"} />
          </Typography>
        ) : (
          <span />
        )}
        <IconButton onClick={unpin}>
          <PushPin
            fontSize={"medium"}
            sx={{
              color: isPinned ? red[700] : isHovered ? "action.active" : "action.disabled",
              animation:
                isHovered && hoveredItem?.model !== pinnedItem?.model ? `${tiltAnimation} 800ms ease-in-out 2` : "none",
              transition: "color 0.3s ease-in-out",
            }}
          />
        </IconButton>
      </Box>
      <Collapse in={isPinned} sx={{ width: "100%" }}>
        {page}
      </Collapse>
    </Drawer>
  );
}

export default function UpsellTable() {
  const salesarea = useSalesareaCtx();
  const createUpsell = useCreateUpsell();
  const [variantFilter, setVariantFilter] = useState<("upsell" | "upscale")[]>([]);
  const pagination = usePagination();

  const { data, isSuccess, isError, isFetching } = useFetchUpsellsQuery(
    { salesareaId: salesarea.id, pagination },
    {
      skip: salesarea.id == null,
    }
  );
  const dimensions = useTimetableDimensions();

  const [toggleUpsell] = useToggleUpsellActiveMutation();
  const [deleteUpsell] = useDeleteUpsellMutation();

  return (
    <PinnableItemsProvider availableItems={["flow", "upsell", "triggers"]}>
      <MiniDrawer />
      <Box sx={{ display: "grid", gap: 2 }}>
        <Paper sx={{ width: 1 }}>
          <Toolbar sx={{ display: "grid", gap: 2, py: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                color={"success"}
                onClick={async () => {
                  await createUpsell();
                }}
                variant={"contained"}
              >
                <Add /> Add an Upsell
              </Button>
              {/*Or*/}
              {/*<Button*/}
              {/*  variant="contained"*/}
              {/*  color="success"*/}
              {/*  onClick={async () => {*/}
              {/*    // await createUpsell();*/}
              {/*  }}*/}
              {/*>*/}
              {/*  <ArrowUpward />*/}
              {/*  Add an Upscale*/}
              {/*</Button>*/}
            </Box>
            {/*
              What filters do we want?
              - Filter on salesarea
              - Filter on timetable
              - Filter on "has product" (upscale only)
              - Filter on "has menu" (upsell only)
              - Filter on name
              - Filter on variant = upsell OR upscale
            */}
            {/*<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>*/}
            {/*  <TextField label="Filter by salesarea"></TextField>*/}
            {/*  <FormControl sx={{ width: 300 }}>*/}
            {/*    <InputLabel id="upsell-variant">Filter by upsell variant</InputLabel>*/}
            {/*    <Select*/}
            {/*      id="upsell-variant"*/}
            {/*      multiple*/}
            {/*      value={variantFilter}*/}
            {/*      label="Filter by upsell variant"*/}
            {/*      onChange={function ({ target: { value } }) {*/}
            {/*        if (value === "upsell" || value === "upscale") setVariantFilter([value]);*/}
            {/*      }}*/}
            {/*      renderValue={(selected) => (*/}
            {/*        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>*/}
            {/*          {selected.map((v) => (*/}
            {/*            <Chip key={v} label={v} />*/}
            {/*          ))}*/}
            {/*        </Box>*/}
            {/*      )}*/}
            {/*    >*/}
            {/*      <MenuItem value="upsell">Upsell</MenuItem>*/}
            {/*      <MenuItem value="upscale">Upscale</MenuItem>*/}
            {/*    </Select>*/}
            {/*  </FormControl>*/}
            {/*</Box>*/}
          </Toolbar>
        </Paper>

        <Box>
          {isSuccess ? <TimeTable dimensions={dimensions} /> : null}
          <Paper
            id={"anchor-table"}
            sx={{
              p: 2,
              width: "100%",
              display: "grid",
              gridTemplate: `
        "hu hu hu hu hu ht ht ht ht ht ht ht"
        ".  .  .  .  .  mo tu we th fr sa su"
        `,
              gridTemplateColumns: "repeat(12, 1fr)",
            }}
          >
            <Typography fontSize={"1.2em"} fontWeight={"bold"} sx={{ gridArea: "hu" }}>
              Upsells
            </Typography>
            <Typography fontSize={"1.2em"} fontWeight={"bold"} sx={{ gridArea: "ht" }}>
              Timeschedule
            </Typography>
            <span style={{ gridColumn: "1 span 5" }} />
            <Typography id={"anchor-first-day"} sx={{ gridArea: "mo" }}>
              Mon
            </Typography>
            <Typography sx={{ gridArea: "tu" }}>Tue</Typography>
            <Typography sx={{ gridArea: "we" }}>Wed</Typography>
            <Typography sx={{ gridArea: "th" }}>Thu</Typography>
            <Typography sx={{ gridArea: "fr" }}>Fri</Typography>
            <Typography sx={{ gridArea: "sa" }}>Sat</Typography>
            <Typography id={"anchor-last-day"} sx={{ gridArea: "su" }}>
              Sun
            </Typography>

            {isFetching
              ? "Loading"
              : isSuccess
                ? data!.data.map((upsell, upsellIdx) => {
                    const numProductTriggers = upsell?.trigger_products?.length ?? 0;
                    const numMenuTriggers = upsell?.trigger_menus?.length ?? 0;
                    const isLastUpsell = upsellIdx === data!.data.length - 1;

                    return (
                      <>
                        <Box
                          sx={{
                            wordWrap: "break-word",
                            padding: "12px 0",
                            display: "grid",
                            gridTemplateColumns: "3fr 1fr",
                            gridColumn: "1 / span 5",
                            borderTop: "1px solid #0002",
                            borderBottom: isLastUpsell ? "1px solid #0002" : undefined,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ fontSize: "1.5em", display: "flex", alignItems: "baseline", gap: 1 }}
                              variant={"h2"}
                            >
                              <ModelInstance
                                instance={upsell}
                                mark={"none"}
                                modelName={"Upsell"}
                                showIdWhen={"always"}
                              />
                            </Typography>
                            <Pinnable model={upsell} name={"triggers"}>
                              <Typography
                                component={"p"}
                                sx={{
                                  "&:hover": {
                                    cursor: "pointer",
                                  },
                                }}
                                variant={"caption"}
                              >
                                Triggered by {numProductTriggers} {numProductTriggers === 1 ? "product" : "products"}{" "}
                                and {numMenuTriggers} {numMenuTriggers === 1 ? "menu" : "menus"}
                              </Typography>
                            </Pinnable>
                          </Box>

                          <Box sx={{ justifySelf: "end", display: "flex", flexDirection: "column" }}>
                            <Switch
                              checked={upsell.activated}
                              onChange={async () => {
                                try {
                                  await toggleUpsell({ id: upsell.id }).unwrap();
                                  // TODO: Automatic(er) toast system for updates
                                  toast.success(() => (
                                    <Typography variant={"body1"}>
                                      Upsell{" "}
                                      <ModelInstance
                                        inheritColor
                                        instance={upsell}
                                        mark={"quoted"}
                                        modelName={"Upsell"}
                                        showIdWhen={"always"}
                                      />{" "}
                                      has been toggled {upsell.activated ? "off" : "on"}
                                    </Typography>
                                  ));
                                } catch (e) {
                                  toast.error(() => (
                                    <Typography>
                                      Was unable to toggle upsell{" "}
                                      <ModelInstance
                                        inheritColor
                                        instance={upsell}
                                        mark={"quoted"}
                                        modelName={"Upsell"}
                                        showIdWhen={"always"}
                                      />
                                      .
                                    </Typography>
                                  ));
                                }
                              }}
                              size={"small"}
                            />
                            <IconButton
                              onClick={async () => {
                                try {
                                  await deleteUpsell({ id: upsell.id }).unwrap();
                                  toast.success(() => (
                                    <Typography>
                                      Upsell{" "}
                                      <ModelInstance
                                        inheritColor
                                        instance={upsell}
                                        mark={"quoted"}
                                        modelName={"Upsell"}
                                        removed
                                        showIdWhen={"always"}
                                      />{" "}
                                      has been deleted.
                                    </Typography>
                                  ));
                                } catch (e) {
                                  toast.error(() => (
                                    <Typography>
                                      Was unable to delete upsell{" "}
                                      <ModelInstance
                                        inheritColor
                                        instance={upsell}
                                        mark={"quoted"}
                                        modelName={"Upsell"}
                                        removed
                                        showIdWhen={"always"}
                                      />
                                      .
                                    </Typography>
                                  ));
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            gridColumn: "6 / span 7",
                            borderTop: "1px solid #0002",
                            borderBottom: isLastUpsell ? "1px solid #0002" : undefined,
                            height:
                              upsell?.flows?.length > 3
                                ? dimensions.calculateRowHeight(upsell.flows.length)
                                : undefined,
                          }}
                        >
                          <Flow dimensions={dimensions} flows={upsell.flows} parentUpsell={upsell} />
                        </Box>
                      </>
                    );
                  })
                : `success: ${isSuccess} - error: ${isError}`}
          </Paper>
        </Box>
      </Box>
      <Paper sx={{ mt: 2 }}>
        <Pagination paginationResponse={data} {...pagination} />
      </Paper>
    </PinnableItemsProvider>
  );
}
